import { useState } from "react";
import * as XLSX from "xlsx";

const TEMPLATE_COLUMNS = [
  "encounter #",
  "date of service",
  "provider 1",
  "cpt billed",
  "billed units",
];

const EXCEL_EXTS = [".xls", ".xlsx", ".csv"];

export const isExcelOrCsv = (filename) =>
  EXCEL_EXTS.some((ext) => (filename || "").toLowerCase().endsWith(ext));

const readFirstRowHeaders = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1 });
        const headers = (rows?.[0] || []).map((c) => String(c).trim().toLowerCase());
        resolve(headers);
      } catch {
        resolve([]);
      }
    };
    reader.onerror = () => resolve([]);
    reader.readAsArrayBuffer(file);
  });

const hasTemplateColumns = (headers) =>
  TEMPLATE_COLUMNS.every((col) => headers.includes(col));

const useTemplateValidation = () => {
  const [softWarnFiles, setSoftWarnFiles] = useState([]);

  const validateFiles = async (rawFiles) => {
    const toAdd = [];    // excel/csv files that are missing columns
    const toRemove = []; // excel/csv files that are now valid (remove old warning)

    for (const file of rawFiles) {
      // Only process Excel/CSV — ignore pdf, doc, hl7, etc. entirely
      if (!isExcelOrCsv(file.name)) continue;

      const headers = await readFirstRowHeaders(file);
      if (!hasTemplateColumns(headers)) {
        toAdd.push(file.name);
      } else {
        // File is valid — make sure any previous warning for this name is cleared
        toRemove.push(file.name);
      }
    }

    setSoftWarnFiles((prev) => {
      // Remove newly-valid files, then add newly-invalid ones (deduped)
      const cleaned = prev.filter((n) => !toRemove.includes(n));
      const existing = new Set(cleaned);
      toAdd.forEach((n) => existing.add(n));
      return [...existing];
    });
  };

  const removeFile = (filename) =>
    setSoftWarnFiles((prev) => prev.filter((n) => n !== filename));

  const clearAll = () => setSoftWarnFiles([]);

  const showSoftWarning = softWarnFiles.length > 0;

  // Call this whenever uploadedCharts changes, passing current chart names
  const syncWithUploaded = (uploadedFileNames) => {
    setSoftWarnFiles((prev) =>
      prev.filter((n) => uploadedFileNames.includes(n))
    );
  };

  return { validateFiles, removeFile, clearAll, showSoftWarning, softWarnFiles, syncWithUploaded };
};

export default useTemplateValidation;